import jp from "jsonpath";
import type { JSONSchema7 } from "json-schema";

export type InitializeNullArrays = (
  data: any,
  schema: Record<string, any>,
  definitions: JSONSchema7["definitions"]
) => any;

export const getObjectSchemaAndData = (
  jsonSchema: JSONSchema7,
  propertyName: string,
  formData: Record<string, any> = {},
  initializeNullArrays: InitializeNullArrays = (data) => data
) => {
  let schema: Record<string, any> = {
    type: ["object", "null"],
    definitions: jsonSchema.definitions,
  };

  // Find property schema by json query
  const objectSchema = jp.query(jsonSchema, `$.properties.${propertyName}`)[0];

  // Set the title on the schema so RJSF templates can use it
  schema.title = objectSchema.title || propertyName;

  // add property definition to schema definitions if $ref exists
  if (objectSchema.$ref) {
    // single object
    const definitionPathComponents = objectSchema.$ref.split("/").slice(1);
    const definitionPath = "$." + definitionPathComponents.join(".");
    const propertyDefinition = jp.query(jsonSchema, definitionPath)[0];
    if (!propertyDefinition) {
      throw new Error(
        `getObjectSchemaAndData: could not resolve $ref "${objectSchema.$ref}" — definition path "${definitionPath}" returned no match.`
      );
    }
    schema.properties = propertyDefinition.properties;
  } else if (objectSchema.items) {
    let itemDefinition = objectSchema.items;
    if (objectSchema.items.$ref) {
      // object is an array - resolve items $ref
      const definitionPathComponents = objectSchema.items.$ref
        .split("/")
        .slice(1);
      const definitionPath = "$." + definitionPathComponents.join(".");
      itemDefinition = jp.query(jsonSchema, definitionPath)[0];
      if (!itemDefinition) {
        throw new Error(
          `getObjectSchemaAndData: could not resolve array items $ref "${objectSchema.items.$ref}" — definition path "${definitionPath}" returned no match.`
        );
      }
    }

    // Patch item definition to ensure arrays with enums have uniqueItems: true
    // This forces RJSF to render them as checkboxes/multi-selects instead of array items
    const patchedItemDefinition = JSON.parse(JSON.stringify(itemDefinition));

    if (patchedItemDefinition.properties) {
      Object.keys(patchedItemDefinition.properties).forEach((key) => {
        const prop = patchedItemDefinition.properties[key];
        const isArray =
          prop.type === "array" ||
          (Array.isArray(prop.type) && prop.type.includes("array"));
        if (isArray && prop.items && prop.items.enum) {
          prop.uniqueItems = true;
        }
        // Patch nested arrays to be just "array" type (not nullable) so RJSF can add items
        if (isArray) {
          prop.type = "array";
          prop.default = [];
        }
      });
    }

    schema.items = patchedItemDefinition;
    schema.type = ["array", "null"];
  }

  // Query formData for each property and initialize null arrays
  let data = formData[propertyName] || [];

  // For array data, initialize null nested arrays in each item
  if (Array.isArray(data) && schema.items) {
    data = data.map((item: any) =>
      initializeNullArrays(item, schema.items, jsonSchema.definitions)
    );
  }

  return { schema, data };
};
